import axios from 'axios';
import { uiText } from '../i18n/uiText';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const API_TIMEOUT = Number(process.env.REACT_APP_API_TIMEOUT_MS || 45000);
const api = axios.create({
  baseURL: API_BASE,
  timeout: Number.isFinite(API_TIMEOUT) ? API_TIMEOUT : 45000,
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-CSRF-Token',
});

export function getApiErrorDetail(err) {
  return err?.response?.data?.detail ?? null;
}

export function getApiErrorMessage(err) {
  return uiText(rawApiErrorMessage(err));
}

function rawApiErrorMessage(err) {
  if (!err) return 'Не удалось выполнить действие. Попробуйте ещё раз.';
  const status = err.response?.status;
  if (status >= 500) return 'Сервис временно не смог подтвердить выполнение запроса. Не закрывайте заполненную форму и повторите действие через минуту.';
  const detail = getApiErrorDetail(err);
  if (detail) {
    if (Array.isArray(detail)) {
      const validationMessages = detail
        .map((item) => {
          const field = Array.isArray(item?.loc) ? item.loc.filter((part) => part !== 'body').join(' → ') : '';
          const message = item?.msg || item?.detail;
          return message ? `${field ? `${field}: ` : ''}${uiText(message)}` : '';
        })
        .filter(Boolean);
      if (validationMessages.length) return uiText('Проверьте заполнение формы: {messages}', { messages: validationMessages.join('; ') });
    }
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object') {
      if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
      if (typeof detail.detail === 'string' && detail.detail.trim()) return detail.detail;
    }
  }
  if (err.response?.data?.message) return err.response.data.message;
  if (err.response?.status === 403) return 'Недостаточно прав для выполнения этого действия.';
  if (err.response?.status === 404) return 'Запрошенные данные не найдены. Возможно, запись была удалена или перемещена.';
  if (err.response?.status === 409) return 'Действие конфликтует с текущим состоянием записи. Обновите страницу и повторите попытку.';
  if (err.response?.status === 422) return 'Не все поля заполнены корректно. Проверьте обязательные данные.';
  if (err.code === 'ECONNABORTED') return 'Сервер отвечает слишком долго. Попробуйте ещё раз.';
  if (err.message === 'Network Error') return 'Нет соединения с сервером.';
  return err.message || 'Не удалось выполнить действие. Попробуйте ещё раз.';
}

function dispatchAppEvent(name, detail) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }
}

const _responseCache = new Map();
const _inflightCache = new Map();
let sessionRevision = 0;

// A cache belongs to one verified UI session, not to the JavaScript module's
// lifetime. Late responses from a previous account must not repopulate it or
// force the current account to log out.
export function resetApiSession() {
  sessionRevision += 1;
  _responseCache.clear();
  _inflightCache.clear();
}

api.interceptors.request.use(cfg => ({ ...cfg, auditSessionRevision: sessionRevision }));
const staleSession = config => config?.auditSessionRevision !== undefined && config.auditSessionRevision !== sessionRevision;
const obsoleteRequest = () => new axios.CanceledError('Устаревший запрос предыдущей сессии');

function stableStringify(value) {
  if (value == null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

function cacheKey(path, params) {
  return `${path}::${stableStringify(params || {})}`;
}

function invalidateCacheByPrefix(prefix) {
  Array.from(_responseCache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) _responseCache.delete(key);
  });
  Array.from(_inflightCache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) _inflightCache.delete(key);
  });
}

function cachedGet(path, params, ttlMs = 15000) {
  const key = cacheKey(path, params);
  const now = Date.now();
  const cached = _responseCache.get(key);
  if (cached && cached.expiresAt > now) {
    return Promise.resolve(cached.response);
  }
  if (_inflightCache.has(key)) return _inflightCache.get(key);
  const revision = sessionRevision;
  const request = api.get(path, { params }).then((response) => {
    if (revision !== sessionRevision) throw obsoleteRequest();
    // A write may have invalidated this read while a newer read is in flight.
    if (_inflightCache.get(key) === request) {
      _responseCache.set(key, { response, expiresAt: Date.now() + ttlMs });
      _inflightCache.delete(key);
    }
    return response;
  }).catch((error) => {
    if (_inflightCache.get(key) === request) _inflightCache.delete(key);
    throw error;
  });
  _inflightCache.set(key, request);
  return request;
}

api.interceptors.response.use(
  (r) => {
    if (staleSession(r.config)) throw obsoleteRequest();
    return r;
  },
  (err) => {
    if (staleSession(err.config)) return Promise.reject(obsoleteRequest());
    const status = err.response?.status;
    if (status === 401) {
      try { localStorage.removeItem('user'); } catch { /* Storage can be unavailable in private browsing. */ }
      dispatchAppEvent('app:auth-required', { path: window.location.pathname });
    } else if (status === 403) {
      // Another tab may have replaced the shared HttpOnly session cookie.
      // Keep the displayed role and available actions in sync with the server.
      dispatchAppEvent('app:auth-refresh', { path: window.location.pathname });
    }
    err.friendlyMessage = getApiErrorMessage(err);
    return Promise.reject(err);
  }
);

export const authApi = {
  login: (username, password) => {
    const form = new FormData();
    form.append('username', username);
    form.append('password', password);
    return api.post('/auth/login', form);
  },
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (data) => api.post('/auth/change-password', data),
  requestPasswordReset: (data) => api.post('/auth/reset-password/request', data),
};

export const presenceApi = {
  heartbeat: (app, clientId) => api.post('/presence/heartbeat', { app, client_id: clientId }),
  online: () => api.get('/presence/online'),
};

export const systemControlApi = {
  overview: () => api.get('/admin/system-control/overview'),
  workflowAudit: () => api.get('/admin/system-control/workflow-audit'),
};

export const auditsApi = {
  list: (options = {}) => cachedGet('/audits/', { detailed: options.detailed ? 1 : undefined }, options.ttlMs || 15000),
  get: (id) => api.get(`/audits/${id}`),
  create: (data) => api.post('/audits/', data).then((res) => { invalidateCacheByPrefix('/audits/'); return res; }),
  update: (id, data) => api.put(`/audits/${id}`, data).then((res) => { invalidateCacheByPrefix('/audits/'); invalidateCacheByPrefix('/workspace/'); return res; }),
  delete: (id) => api.delete(`/audits/${id}`).then((res) => { invalidateCacheByPrefix('/audits/'); invalidateCacheByPrefix('/workspace/'); return res; }),
  submitAnswers: (id, answers) => api.post(`/audits/${id}/answers`, answers),
  saveDrafts: (id, answers) => api.post(`/audits/${id}/drafts`, answers),
  getDrafts: (id) => api.get(`/audits/${id}/drafts`),
  clearDrafts: (id) => api.delete(`/audits/${id}/drafts`),
  setQuestionScope: (id, data) => api.put(`/audits/${id}/question-scope`, data),
  createExtraQuestion: (id, data) => api.post(`/audits/${id}/extra-questions`, data),
  removeQuestion: (id, questionId, reason = null) => api.delete(`/audits/${id}/questions/${questionId}`, { data: { reason } }).then((res) => { invalidateCacheByPrefix('/audits/'); invalidateCacheByPrefix('/workspace/'); return res; }),
  removeExtraQuestion: (id, questionId, reason = null) => api.delete(`/audits/${id}/extra-questions/${questionId}`, { data: { reason } }).then((res) => { invalidateCacheByPrefix('/audits/'); invalidateCacheByPrefix('/workspace/'); return res; }),
  expectedAnswers: (id) => api.get(`/audits/${id}/expected-answers`),
  saveExpectedAnswer: (id, data) => api.put(`/audits/${id}/expected-answers`, data),
  generateExpectedAnswer: (id, data) => api.post(`/audits/${id}/expected-answers/generate`, data, { timeout: 720000 }),
  listEditAccessRequests: (id) => api.get(`/audits/${id}/edit-access-requests`),
  requestEditAccess: (id, data) => api.post(`/audits/${id}/request-edit-access`, data || {}),
  approveEditAccess: (id, requestId, data) => api.post(`/audits/${id}/edit-access-requests/${requestId}/approve`, data || {}),
  rejectEditAccess: (id, requestId, data) => api.post(`/audits/${id}/edit-access-requests/${requestId}/reject`, data || {}),
  // Справочник: типы аудитов / типы объектов / профили регламентов / классификация находок.
  // Используется формой создания аудита, чтобы один список был на бэке и фронте.
  taxonomy: (options = {}) => cachedGet('/audits/taxonomy', undefined, options.ttlMs || 600000),
  // Сводка по индексу соответствия (ИС): количество КН/ЗН/НН + рассчитанный балл.
  // Используется в табе «Сводка ИС» страницы аудита и при финализации акта.
  complianceSummary: (id) => api.get(`/audits/${id}/compliance-summary`),
  // Приостановить аудит (форс-мажор / отказ предоставить доступ)
  suspend: (id, data) => api.post(`/audits/${id}/suspend`, data).then((res) => { invalidateCacheByPrefix('/audits/'); return res; }),
  // Возобновить приостановленный аудит
  resume: (id) => api.post(`/audits/${id}/resume`).then((res) => { invalidateCacheByPrefix('/audits/'); return res; }),
  sendProgram: (id) => api.post(`/audits/${id}/send-program`).then((res) => { invalidateCacheByPrefix('/audits/'); invalidateCacheByPrefix('/workspace/'); return res; }),
  confirmProgram: (id, data) => api.post(`/audits/${id}/confirm-program`, data).then((res) => { invalidateCacheByPrefix('/audits/'); invalidateCacheByPrefix('/workspace/'); return res; }),
};

export const templatesApi = {
  generateExpectedAnswer: (data, config = {}) => api.post('/templates/expected-answer/generate', data, { timeout: 300000, ...config }),
  list: (status, options = {}) => {
    const params = {};
    if (status) params.status = status;
    if (options.audit_kind) params.audit_kind = options.audit_kind;
    if (options.summary) params.summary = true;
    return cachedGet('/templates/', Object.keys(params).length ? params : undefined, options.ttlMs || 30000);
  },
  get: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates/', data).then((res) => { invalidateCacheByPrefix('/templates/'); return res; }),
  update: (id, data) => api.put(`/templates/${id}`, data).then((res) => { invalidateCacheByPrefix('/templates/'); return res; }),
  saveNewCompat: (data, publish = false) => api.put(publish ? '/templates/new/publish' : '/templates/new', data).then((res) => { invalidateCacheByPrefix('/templates/'); return res; }),
  delete: (id) => api.delete(`/templates/${id}`).then((res) => { invalidateCacheByPrefix('/templates/'); return res; }),
  publish: (id) => api.post(`/templates/${id}/publish`).then((res) => { invalidateCacheByPrefix('/templates/'); return res; }),
  downloadExcelTemplate: () => api.get('/templates/excel-template', { responseType: 'blob' }),
  importExcel: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/templates/import-excel', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ====== Договоры-основания аудитов ======
// По обоим регламентам аудит проводится только при наличии договорного
// основания (договор оказания услуг ААС, рамочный договор поставки/подряда,
// договор коммерческой концессии или иной сетевой договор).
export const contractsApi = {
  list: (params, options = {}) => cachedGet('/contracts/', params, options.ttlMs || 30000),
  get: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts/', data).then((res) => { invalidateCacheByPrefix('/contracts/'); return res; }),
  update: (id, data) => api.put(`/contracts/${id}`, data).then((res) => { invalidateCacheByPrefix('/contracts/'); return res; }),
  delete: (id) => api.delete(`/contracts/${id}`).then((res) => { invalidateCacheByPrefix('/contracts/'); return res; }),
};

// ====== Подписанты с доверенностями ======
// Уполномоченные лица компании, имеющие право подписи ОРД-документов.
// При оформлении приказа/акта/предписания PDF забирает их из этого реестра.
export const signatoriesApi = {
  list: (params, options = {}) => cachedGet('/signatories/', params, options.ttlMs || 30000),
  get: (id) => api.get(`/signatories/${id}`),
  create: (data) => api.post('/signatories/', data).then((res) => { invalidateCacheByPrefix('/signatories/'); return res; }),
  update: (id, data) => api.put(`/signatories/${id}`, data).then((res) => { invalidateCacheByPrefix('/signatories/'); return res; }),
  delete: (id) => api.delete(`/signatories/${id}`).then((res) => { invalidateCacheByPrefix('/signatories/'); return res; }),
};

// ====== Подписи / ознакомления с документами ======
// Журнал того, кто, когда и как подписал/ознакомился с конкретным
// документом аудита. Используется для юридической трассируемости.
export const documentSignaturesApi = {
  list: (auditId, documentKind) => api.get('/document-signatures/', {
    params: { audit_id: auditId, document_kind: documentKind },
  }),
  create: (data) => api.post('/document-signatures/', data),
  pdf: (id) => api.get(`/document-signatures/${id}/pdf`, { responseType: 'blob' }),
  revoke: (id) => api.delete(`/document-signatures/${id}`),
};

// Self-check опросник (UC-1.1, Приложение А FTT)
export const selfCheckApi = {
  // Структура опросника (12 вопросов)
  definition: () => api.get('/self-check/definition'),
  // Auditee
  my: () => api.get('/self-check/my'),
  saveAnswers: (answers) => api.post('/self-check/my', { answers }),
  submit: () => api.post('/self-check/my/submit'),
  uploadDocument: (formData) => api.post('/self-check/my/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteDocument: (id) => api.delete(`/self-check/my/documents/${id}`),
  // Admin/Auditor
  getCompany: (companyId) => api.get(`/self-check/${companyId}`),
  review: (checkId, notes) => api.post(`/self-check/${checkId}/review`, { notes }),
};

// Модуль аналитики: задачи, шаблоны чек-листов, заполненные формы и файлы.
export const analyticsApi = {
  reference: () => api.get('/analytics/reference'),
  users: () => api.get('/analytics/users'),
  dashboard: (params) => api.get('/analytics/dashboard', { params }),
  tasks: (params) => api.get('/analytics/tasks', { params }),
  getTask: (id) => api.get(`/analytics/tasks/${id}`),
  createTask: (data) => api.post('/analytics/tasks', data),
  updateTask: (id, data) => api.put(`/analytics/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/analytics/tasks/${id}`),
  bulkUpdateTasks: (data) => api.post('/analytics/tasks/bulk-update', data),
  templates: (params) => api.get('/analytics/form-templates', { params }),
  getTemplate: (id) => api.get(`/analytics/form-templates/${id}`),
  createTemplate: (data) => api.post('/analytics/form-templates', data),
  updateTemplate: (id, data) => api.put(`/analytics/form-templates/${id}`, data),
  publishTemplate: (id) => api.post(`/analytics/form-templates/${id}/publish`),
  archiveTemplate: (id) => api.post(`/analytics/form-templates/${id}/archive`),
  submissions: (taskId) => api.get(`/analytics/tasks/${taskId}/submissions`),
  submitForm: (taskId, data) => api.post(`/analytics/tasks/${taskId}/submissions`, data),
  requestClarification: (submissionId, data) => api.post(`/analytics/submissions/${submissionId}/request-clarification`, data),
  approveSubmission: (submissionId) => api.post(`/analytics/submissions/${submissionId}/approve`),
  files: (taskId) => api.get(`/analytics/tasks/${taskId}/files`),
  uploadFile: (taskId, data) => api.post(`/analytics/tasks/${taskId}/files`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteFile: (fileId) => api.delete(`/analytics/files/${fileId}`),
  comments: (taskId) => api.get(`/analytics/tasks/${taskId}/comments`),
  addComment: (taskId, data) => api.post(`/analytics/tasks/${taskId}/comments`, data),
};

export const localAiApi = {
  status: () => api.get('/local-ai/status'),
  conversations: () => api.get('/local-ai/conversations'),
  createConversation: (data = {}) => api.post('/local-ai/conversations', data),
  getConversation: (id) => api.get(`/local-ai/conversations/${id}`),
  archiveConversation: (id) => api.delete(`/local-ai/conversations/${id}`),
  ask: (id, message, options = {}) => api.post(
    `/local-ai/conversations/${id}/messages`,
    { message, document_ids: options.documentIds ?? null },
    { timeout: 720000 },
  ),
  askAsync: (id, message, options = {}) => api.post(
    `/local-ai/conversations/${id}/messages/async`,
    { message, document_ids: options.documentIds ?? null },
    { timeout: 30000 },
  ),
  documents: () => api.get('/local-ai/documents'),
  uploadDocument: (file, onUploadProgress) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/local-ai/documents/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 300000,
    });
  },
  cancelDocument: (id) => api.post(`/local-ai/documents/${id}/cancel`),
  deleteDocument: (id) => api.delete(`/local-ai/documents/${id}`),
  indexAudits: () => api.post('/local-ai/index-audits'),
};

// ERP-флаги (UC-9.3): передача в учётную систему при эскалации
// ERP-флаги (UC-9.3): передача в учётную систему при эскалации
export const erpFlagsApi = {
  list: (params) => api.get('/erp-flags/', { params }),
  create: (data) => api.post('/erp-flags/', data),
  acknowledge: (id) => api.post(`/erp-flags/${id}/acknowledge`),
  retry: (id) => api.post(`/erp-flags/${id}/retry`),
  // UC-9.3: подтверждение/отклонение автоэскалированных флагов
  confirmAndSend: (id) => api.post(`/erp-flags/${id}/confirm-and-send`),
  dismiss: (id) => api.post(`/erp-flags/${id}/dismiss`),
};

// Сводка по компаниям «Светофор» (UC-8.1). URL сохранён для совместимости.
export const executiveDashboardApi = {
  tiles: () => api.get('/dashboard/executive/tiles'),
  criticalTop: (limit = 5) => api.get('/dashboard/executive/critical-top', { params: { limit } }),
  dynamics: (days = 90) => api.get('/dashboard/executive/dynamics', { params: { days } }),
  companiesSummary: (ratingColor) => api.get('/dashboard/executive/companies-summary', {
    params: ratingColor ? { rating_color: ratingColor } : undefined,
  }),
  // UC-8.2: экспорт сводного отчёта в Excel
  exportXlsx: () => api.get('/dashboard/executive/export-xlsx', { responseType: 'blob' }),
};

// История рейтинга компаний (раунд 10: годовой тренд)
export const companyRatingApi = {
  history: (companyId, days = 365) => api.get(`/companies/${companyId}/rating-history`, { params: { days } }),
  setRating: (companyId, data) => api.post(`/companies/${companyId}/rating`, data),
};

// Admin: настройка весов self-check (FTT 3.1.4)
export const selfCheckWeightsApi = {
  get: () => api.get('/admin/self-check-weights/'),
  update: (data) => api.put('/admin/self-check-weights/', data),
  reset: () => api.post('/admin/self-check-weights/reset'),
};

export const usersApi = {
  list: () => api.get('/users/'),
  passwordResetRequests: (status = 'all') => api.get('/users/password-reset-requests', { params: { status } }),
  decidePasswordReset: (requestId, decision) => api.post(`/users/password-reset-requests/${requestId}/decision`, { decision }),
  create: (data) => api.post('/auth/register', data),
  auditors: (options = {}) => cachedGet('/users/auditors', undefined, options.ttlMs || 30000),
  customers: (options = {}) => cachedGet('/users/customers', undefined, options.ttlMs || 30000),
  update: (id, data) => api.put(`/users/${id}`, data),
  forcePasswordChange: (id) => api.post(`/users/${id}/force-password-change`),
  revokeSessions: (id) => api.post(`/users/${id}/revoke-sessions`),
  delete: (id) => api.delete(`/users/${id}`),
};

export const commentsApi = {
  create: (data) => api.post('/comments/', data),
  delete: (id) => api.delete(`/comments/${id}`),
};

export const reportsApi = {
  compare: (firstId, secondId, config = {}) => api.get(`/reports/${firstId}/compare/${secondId}`, config),
  pdf: (auditId) => api.get(`/reports/${auditId}/pdf`, { responseType: 'blob' }),
  checklistXlsx: (auditId) => api.get(`/reports/${auditId}/checklist.xlsx`, { responseType: 'blob' }),
  questions: (auditId, config = {}) => api.get(`/reports/${auditId}/questions`, config),
  export: (auditId, data, config = {}) => api.post(`/reports/${auditId}/export`, data, { ...config, responseType: 'blob', timeout: 180000 }),
};

export const filesApi = {
  annotations: (id, config = {}) => api.get(`/files/${id}/annotations`, config),
  annotate: (id, data, config = {}) => api.post(`/files/${id}/annotations`, data, config),
  upload: ({ auditId, questionId = null, extraQuestionId = null, issueId = null, file, onUploadProgress, purpose = null }) => {
    const form = new FormData();
    if (auditId != null) form.append('audit_id', auditId);
    if (questionId != null) form.append('question_id', questionId);
    if (extraQuestionId != null) form.append('extra_question_id', extraQuestionId);
    if (issueId != null) form.append('issue_id', issueId);
    if (purpose != null) form.append('purpose', purpose);
    form.append('file', file);
    return api.post('/files/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
  // FTT UC-5.2: загрузка через FormData напрямую (для скан-подписи и т.п.)
  uploadFormData: (formData) => api.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/files/${id}`),
  download: (id) => api.get(`/files/${id}/download`, { responseType: 'blob' }),
};

export const issuesApi = {
  list: (auditId) => api.get('/issues/', { params: auditId ? { audit_id: auditId } : {} }),
  create: (data) => api.post('/issues/', data),
  update: (id, data) => api.put(`/issues/${id}`, data),
  addMessage: (id, data) => api.post(`/issues/${id}/messages`, data),
  delete: (id) => api.delete(`/issues/${id}`),
};

export const searchApi = {
  query: (q) => api.get('/search/', { params: { q } }),
  savedViews: () => api.get('/search/saved-views'),
  saveView: (data) => api.post('/search/saved-views', data),
  deleteView: (id) => api.delete(`/search/saved-views/${id}`),
};

const sanitizeWorkspaceAuditsParams = (params = {}) => {
  const cleaned = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === 'all') return;
    if (key === 'auditor_id' || key === 'customer_id') {
      const normalized = typeof value === 'string' ? value.trim() : value;
      if (typeof normalized === 'string' && !/^\d+$/.test(normalized)) return;
      const num = Number(normalized);
      if (!Number.isInteger(num) || Number.isNaN(num) || num <= 0) return;
      cleaned[key] = num;
      return;
    }
    cleaned[key] = value;
  });
  return cleaned;
};

export const workspaceApi = {
  dashboard: () => api.get('/workspace/dashboard'),
  audits: (params) => api.get('/workspace/audits', { params: sanitizeWorkspaceAuditsParams(params) }),
  management: () => api.get('/workspace/management'),
  auditProcedure: (auditId) => api.get(`/workspace/audits/${auditId}/procedure`),
  auditDocuments: (auditId) => api.get(`/workspace/audits/${auditId}/documents`),
  generateDocuments: (auditId, data) => api.post(`/workspace/audits/${auditId}/generate-documents`, data),
};

export const notificationsApi = {
  list: (params) => api.get('/notifications/', { params }),
  markRead: (data) => api.post('/notifications/read', data),
  markAllRead: () => api.post('/notifications/read-all'),
};

export const opsApi = {
  dashboardSummary: () => api.get('/ops/dashboard-summary'),
  dashboardAnalytics: (period) =>
    api.get('/ops/dashboard-analytics', { params: period ? { period } : {} }),
  dashboardAnalyticsXlsx: () => api.get('/ops/dashboard-analytics.xlsx', { responseType: 'blob' }),
  auditsXlsx: () => api.get('/ops/audits.xlsx', { responseType: 'blob' }),
  issuesXlsx: () => api.get('/ops/issues.xlsx', { responseType: 'blob' }),
  alertsCenter: () => api.get('/ops/alerts-center'),
  alertsCenterXlsx: () => api.get('/ops/alerts-center.xlsx', { responseType: 'blob' }),
  controlMonitor: () => api.get('/ops/control-monitor'),
  controlMonitorXlsx: () => api.get('/ops/control-monitor.xlsx', { responseType: 'blob' }),
  controlMonitorResync: () => api.post('/ops/control-monitor/resync'),
};

export const companiesApi = {
  list: (options = {}) => cachedGet('/companies/', undefined, options.ttlMs || 30000),
  my: () => api.get('/companies/my'),
  inviteMyMember: (data) => api.post('/companies/my/members', data),
  get: (id) => api.get(`/companies/${id}`),
  members: (id) => api.get(`/companies/${id}/members`),
  createMember: (id, data) => api.post(`/companies/${id}/members`, data).then((res) => { invalidateCacheByPrefix('/companies/'); return res; }),
  create: (data) => api.post('/companies/', data).then((res) => { invalidateCacheByPrefix('/companies/'); return res; }),
  update: (id, data) => api.put(`/companies/${id}`, data).then((res) => { invalidateCacheByPrefix('/companies/'); return res; }),
  delete: (id) => api.delete(`/companies/${id}`).then((res) => { invalidateCacheByPrefix('/companies/'); return res; }),
  // История аудитов компании с расчётом ИС по каждому. Используется на карточке компании.
  audits: (companyId) => api.get(`/audits/by-company/${companyId}`),
  // Краткая сводка по последним аудитам компании + рекомендуемый интервал
  // до следующего. Используется в форме создания нового аудита, чтобы
  // аудитор сразу видел контекст и историю.
  auditsSummary: (companyId) => api.get(`/audits/by-company/${companyId}/summary`),
};

export const auditPlansApi = {
  list: () => api.get('/audit-plans/'),
  get: (id) => api.get(`/audit-plans/${id}`),
  create: (data) => api.post('/audit-plans/', data),
  update: (id, data) => api.put(`/audit-plans/${id}`, data),
  pdf: (id) => api.get(`/audit-plans/${id}/pdf`, { responseType: 'blob' }),
};

export const auditProgramsApi = {
  list: () => api.get('/audit-programs/'),
  get: (id) => api.get(`/audit-programs/${id}`),
  create: (data) => api.post('/audit-programs/', data),
  update: (id, data) => api.put(`/audit-programs/${id}`, data),
  pdf: (id) => api.get(`/audit-programs/${id}/pdf`, { responseType: 'blob' }),
};

export const auditReportsApi = {
  list: () => api.get('/audit-reports/'),
  get: (id) => api.get(`/audit-reports/${id}`),
  create: (data) => api.post('/audit-reports/', data),
  upsert: (data) => api.post('/audit-reports/', data),
  update: (id, data) => api.put(`/audit-reports/${id}`, data),
  pdf: (id) => api.get(`/audit-reports/${id}/pdf`, { responseType: 'blob' }),
};

export const correctivePlansApi = {
  list: (params) => api.get('/corrective-plans/', { params }),
  get: (id) => api.get(`/corrective-plans/${id}`),
  create: (data) => api.post('/corrective-plans/', data),
  upsert: (data) => api.post('/corrective-plans/', data),
  update: (id, data) => api.put(`/corrective-plans/${id}`, data),
  submit: (id) => api.post(`/corrective-plans/${id}/submit`),
  review: (id, data) => api.post(`/corrective-plans/${id}/review`, data),
  reviewItem: (planId, itemId, data) => api.post(`/corrective-plans/${planId}/items/${itemId}/review`, data),
  pdf: (id) => api.get(`/corrective-plans/${id}/pdf`, { responseType: 'blob' }),
  remediationPdf: (id) => api.get(`/corrective-plans/${id}/remediation-pdf`, { responseType: 'blob' }),
};

export const procedureControlApi = {
  list: () => api.get('/procedure-control/'),
  get: (auditId) => api.get(`/procedure-control/${auditId}`),
  create: (data) => api.post('/procedure-control/', data),
  upsert: (data) => api.post('/procedure-control/', data),
};

export const outgoingEmailsApi = {
  list: (status, scope = 'mine') => api.get('/outgoing-emails/', { params: { ...(status ? { status } : {}), scope } }),
  queueRequest: (id) => api.post(`/outgoing-emails/queue/request/${id}`),
  queueProgram: (id, target = 'object') =>
    api.post(`/outgoing-emails/queue/program/${id}`, null, { params: { target } }),
  queueReport: (id, target = 'object') =>
    api.post(`/outgoing-emails/queue/report/${id}`, null, { params: { target } }),
  markSent: (id) => api.post(`/outgoing-emails/${id}/mark-sent`),
  markFailed: (id, error) => api.post(`/outgoing-emails/${id}/mark-failed`, { error }),
  sendNow: (id) => api.post(`/outgoing-emails/${id}/send-now`),
  resend: (id) => api.post(`/outgoing-emails/${id}/resend`),
};

export const procedureWorkflowApi = {
  list: () => api.get('/procedure-workflow/'),
  getByAudit: (auditId) => api.get(`/procedure-workflow/audit/${auditId}`),
  saveByAudit: (auditId, data) => api.post(`/procedure-workflow/audit/${auditId}`, data),
};

export const auditMeetingsApi = {
  list: (params) => api.get('/audit-meetings/', { params }),
  create: (data) => api.post('/audit-meetings/', data),
  update: (id, data) => api.put(`/audit-meetings/${id}`, data),
  pdf: (id) => api.get(`/audit-meetings/${id}/pdf`, { responseType: 'blob' }),
};
export const correctiveActionsApi = {
  list: (params) => api.get('/corrective-actions/', { params }),
  get: (id) => api.get(`/corrective-actions/${id}`),
  create: (data) => api.post('/corrective-actions/', data),
  update: (id, data) => api.put(`/corrective-actions/${id}`, data),
};

export const auditorPoolApi = {
  list: (params) => api.get('/auditor-pool/', { params }),
  getProfile: (userId) => api.get(`/auditor-pool/${userId}`),
  updateProfile: (userId, data) => api.put(`/auditor-pool/${userId}`, data),
  setAvailability: (userId, data) => api.post(`/auditor-pool/${userId}/availability`, data),
  applyToAudit: (auditId, data) => api.post(`/auditor-pool/apply/${auditId}`, data),
  schedule: (params) => api.get('/auditor-pool/schedule', { params, paramsSerializer: { indexes: null } }),
};

// Audit assignments: admin assigns auditors; auditors accept/decline
export const auditAssignmentsApi = {
  // List assignments for a given audit (returns list of {id, auditor_user_id, auditor_name, status, note, response_note})
  list: (auditId) => api.get(`/audits/${auditId}/assignments`),

  // Admin assigns auditors to an audit
  // data: { auditor_user_ids: number[], note?: string }
  assign: (auditId, data) => api.post(`/audits/${auditId}/assign`, data).then(res => { invalidateCacheByPrefix('/audits/'); invalidateCacheByPrefix('/workspace/'); return res; }),

  // Auditor responds to an assignment invitation
  // data: { action: 'accept' | 'decline', note?: string }
  respond: (assignmentId, data) => api.post(`/audit-assignments/${assignmentId}/respond`, data),

  // Current user's assignment notifications
  myNotifications: () => api.get('/audit-assignments/my-notifications'),

  // Count of pending notifications for badge
  myPendingCount: () => api.get('/audit-assignments/my-pending-count'),
};

export default api;


export const auditArchiveApi = {
  list: () => api.get('/audit-archive/'),
  getByAudit: (auditId) => api.get(`/audit-archive/audits/${auditId}`),
  archive: (auditId, data) => api.post(`/audit-archive/audits/${auditId}/archive`, data || {}),
  unarchive: (auditId, data) => api.post(`/audit-archive/audits/${auditId}/unarchive`, data || {}),
  legalHold: (auditId, data) => api.post(`/audit-archive/audits/${auditId}/legal-hold`, data || {}),
  syncAll: () => api.post('/audit-archive/sync-all'),
};


// R13: Календарь — API доступности аудиторов + перенос
export const calendarApi = {
  availability: (date) => api.get('/calendar/availability', { params: { date } }),
  auditorLoad: (days = 30) => api.get('/calendar/auditor-load', { params: { days } }),
  reschedule: (data) => api.post('/calendar/reschedule', data).then(res => { invalidateCacheByPrefix('/audits/'); invalidateCacheByPrefix('/workspace/'); return res; }),
  exportXlsx: (year, month) => api.get('/calendar/export.xlsx', { params: { year, month }, responseType: 'blob' }),
};
